import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import config from '../../config/config';
import './ContactPage.css';

const ENT_FEATURES = [
  'On-premise of private cloud deployment',
  'SSO / SAML (Okta, Azure AD, Google Workspace)',
  'Onbeperkte repositories & gebruikers',
  'Dedicated Customer Success Manager',
  'Custom policies & compliance thresholds',
  'SLA 99.99% met support binnen 4 uur',
  'NDA & Data Processing Agreement (DPA)',
  'Pentest rapport op aanvraag',
];

const CONTACT_TYPES = [
  { id: 'enterprise', label: '🏢 Enterprise offerte' },
  { id: 'early',      label: '🚀 Early Adopter deal' },
  { id: 'demo',       label: '🎯 Demo inplannen' },
  { id: 'other',      label: '💬 Overig' },
];

const TEAM_SIZES = [
  '1–10 medewerkers',
  '11–50 medewerkers',
  '51–200 medewerkers',
  '201–1000 medewerkers',
  '1000+ medewerkers',
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const typeParam = searchParams.get('type') || 'enterprise';
  const [contactType, setContactType] = useState(typeParam);
  const [form, setForm] = useState({
    name: '', company: '', email: '', teamSize: '', message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${config.BASE_URL}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company,
        message: form.message,
        contactType,
        phone: '',
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const headings = {
    enterprise: { h: 'Enterprise offerte aanvragen', p: 'Ontvang een voorstel op maat. Ons sales team neemt binnen één werkdag contact op.' },
    early:      { h: '🚀 Claim Early Adopter deal', p: 'Slechts 10 plekken voor €99/maand lifetime Professional. Zit je er bij?' },
    demo:       { h: 'Plan een persoonlijke demo', p: '30 minuten — we tonen DepGuard live aan de hand van jouw use case.' },
    other:      { h: 'Neem contact op', p: 'Vragen, feedback of samenwerking? Stuur ons een bericht.' },
  };

  const { h, p } = headings[contactType] || headings.other;

  return (
    <div className="contact-page">
      {/* ── Left panel ── */}
      <div className="contact-left">
        <div className="contact-logo" onClick={() => navigate('/website')}>
          <span className="contact-logo-icon">🛡️</span>
          <span className="contact-logo-text">DepGuard</span>
        </div>

        <h2>Enterprise-grade SBOM security — op jouw voorwaarden</h2>
        <p>
          Grote organisaties kiezen DepGuard voor on-premise controle, SSO-integratie en dedicated support —
          zonder de complexiteit en prijskaartje van Black Duck of Snyk Enterprise.
        </p>

        <ul className="contact-enterprise-features">
          {ENT_FEATURES.map((f, i) => (
            <li key={i} className="contact-ent-feature">
              <div className="contact-ent-check">✓</div>
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <div className="contact-trust">
          <span className="contact-trust-item">🔒 GDPR-compliant · EU-servers</span>
          <span className="contact-trust-item">📄 DPA beschikbaar</span>
          <span className="contact-trust-item">⚡ Reactie binnen 1 werkdag</span>
          <span className="contact-trust-item">📞 <a href="tel:+31654220645" style={{color:'inherit', textDecoration:'none'}}>+31 6 54 22 06 45</a></span>
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="contact-right">
        {success ? (
          <div className="contact-success">
            <div className="contact-success-icon">
              {contactType === 'early' ? '🚀' : '✅'}
            </div>
            <h2>
              {contactType === 'early'
                ? 'Plek geclaimed!'
                : contactType === 'demo'
                ? 'Demo aangevraagd!'
                : 'Bericht ontvangen!'}
            </h2>
            <p>
              {contactType === 'early'
                ? 'We nemen binnen 24 uur contact op om jouw Early Adopter onboarding in te plannen. Bedankt!'
                : contactType === 'demo'
                ? 'We sturen je binnen één werkdag een uitnodiging voor de demo. Tot snel!'
                : 'Ons team neemt binnen één werkdag contact op via ' + form.email + '.'}
            </p>
            <button className="contact-back-btn" onClick={() => navigate('/pricing')}>
              ← Terug naar Pricing
            </button>
          </div>
        ) : (
          <>
            <div className="contact-form-header">
              <h1>{h}</h1>
              <p>{p}</p>
            </div>

            {/* Type tabs */}
            <div className="contact-type-tabs">
              {CONTACT_TYPES.map(t => (
                <button
                  key={t.id}
                  className={`contact-type-tab${contactType === t.id ? ' active' : ''}`}
                  onClick={() => setContactType(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="contact-field">
                <label>Volledige naam</label>
                <input
                  type="text"
                  placeholder="Jan de Vries"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  required
                  autoFocus
                />
              </div>

              <div className="contact-field">
                <label>Bedrijfsnaam</label>
                <input
                  type="text"
                  placeholder="Jouw bedrijf BV"
                  value={form.company}
                  onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                  required
                />
              </div>

              <div className="contact-field">
                <label>Zakelijk e-mailadres</label>
                <input
                  type="email"
                  placeholder="jan@bedrijf.nl"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>

              <div className="contact-field">
                <label>Teamgrootte</label>
                <select
                  value={form.teamSize}
                  onChange={e => setForm(f => ({ ...f, teamSize: e.target.value }))}
                  required
                >
                  <option value="">Selecteer teamgrootte</option>
                  {TEAM_SIZES.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="contact-field">
                <label>
                  {contactType === 'enterprise' ? 'Wat zijn je belangrijkste requirements?' :
                   contactType === 'early'      ? 'Vertel kort over je use case' :
                   contactType === 'demo'       ? 'Wat wil je zien in de demo?' :
                   'Jouw bericht'}
                </label>
                <textarea
                  placeholder={
                    contactType === 'enterprise'
                      ? 'bijv. on-premise vereist, SSO met Azure AD, 200 repositories...'
                      : contactType === 'early'
                      ? 'bijv. startup, 3 developers, microservices op Node.js en Go...'
                      : contactType === 'demo'
                      ? 'bijv. CI/CD integratie, compliance dashboard, VEX workflow...'
                      : 'Schrijf hier jouw vraag of opmerking...'
                  }
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={4}
                />
              </div>

              {error && <div className="contact-error">⚠️ {error}</div>}

              <button type="submit" className="contact-submit" disabled={loading}>
                {loading ? 'Verzenden…' :
                 contactType === 'enterprise' ? 'Offerte aanvragen →' :
                 contactType === 'early'      ? '🚀 Claim mijn Early Adopter plek →' :
                 contactType === 'demo'       ? 'Demo inplannen →' :
                 'Bericht versturen →'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20, fontSize: '0.8rem', color: '#475569' }}>
              Wil je direct een trial starten?{' '}
              <button
                style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', fontWeight: 600, fontSize: 'inherit' }}
                onClick={() => navigate('/trial?plan=professional')}
              >
                14 dagen gratis proberen →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
